import { API_BASE_URL, ApiError, api } from './client'

import type { components } from '@/lib/api/generated/schema'

/** Loại hoạt động: nhiệm vụ, lịch công tác, lịch họp, sự kiện. */
export type WorkActivityType = 'task' | 'business_trip' | 'meeting' | 'event'

/** Trạng thái nhiệm vụ; hai loại lịch luôn null. */
export type WorkActivityStatus = 'open' | 'in_progress' | 'done' | 'closed'

/** Bốn cột của bảng Kanban, theo đúng thứ tự hiển thị. */
export const WORK_ACTIVITY_STATUSES: WorkActivityStatus[] = [
    'open',
    'in_progress',
    'done',
    'closed',
]

export type WorkActivity = components['schemas']['WorkActivity']
export type WorkActivityAssignee = components['schemas']['WorkActivityAssignee']
export type WorkActivityRequest = components['schemas']['WorkActivityRequest']
export type WorkActivityGuest = components['schemas']['WorkActivityGuest']
export type WorkActivityFile = components['schemas']['WorkActivityFile']

/**
 * Link tải một tệp đính kèm qua route của CHÍNH hoạt động đó: quyền tải đi theo
 * quyền xem hoạt động, không theo quyền files.read chung. Trình duyệt tự gửi
 * cookie phiên khi mở link nên dùng thẳng cho <a href> và <img src>.
 */
export function workActivityFileUrl(activityId: string, fileId: string) {
    return `${API_BASE_URL}/work-activities/${activityId}/files/${fileId}/download`
}

export interface WorkActivityListParams {
    /** Lọc theo loại; nhiều loại nối bằng dấu phẩy. */
    type?: string
    /** Lọc theo trạng thái; nhiều trạng thái nối bằng dấu phẩy. */
    status?: string
    /** Đầu khung nhìn của màn lịch (ISO 8601). */
    from?: string
    /** Cuối khung nhìn của màn lịch (ISO 8601). */
    to?: string
    assignee_id?: string
    /** Trần số bản ghi (1–1000). Bỏ trống = không giới hạn. */
    limit?: number
}

export const workActivityApi = {
    list: (params?: WorkActivityListParams) =>
        api.get<WorkActivity[]>('/work-activities', { params }),

    get: (id: string) => api.get<WorkActivity>(`/work-activities/${id}`),

    create: (body: WorkActivityRequest) =>
        api.post<WorkActivity>('/work-activities', body),

    update: (id: string, body: WorkActivityRequest) =>
        api.put<WorkActivity>(`/work-activities/${id}`, body),

    remove: (id: string) => api.delete<void>(`/work-activities/${id}`),

    /**
     * Tải một tệp đính kèm lên TRƯỚC khi lưu form (form tạo mới chưa có id
     * hoạt động); gắn vào hoạt động bằng `file_ids` khi lưu.
     */
    uploadFile: async (file: File): Promise<WorkActivityFile> => {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post<WorkActivityFile>(
            '/work-activities/files',
            formData,
        )
        if (!res.data) {
            throw new ApiError(
                0,
                'UPLOAD_NO_RECORD',
                'Upload succeeded but no file record was returned',
            )
        }
        return res.data
    },

    /**
     * Bảng công việc của màn máy trạm: chỉ đọc, không tham số, không lọc theo
     * người dùng. "Hôm nay" do server quyết định theo lịch Việt Nam để mọi máy
     * trạm luôn nói cùng một ngày.
     */
    listForStation: () => api.get<WorkActivity[]>('/station/work-activities'),

    /**
     * Kéo-thả trên bảng: gửi kèm trạng thái đang thấy để backend từ chối khi
     * người khác vừa kéo thẻ trước (409).
     */
    changeStatus: (
        id: string,
        fromStatus: WorkActivityStatus,
        toStatus: WorkActivityStatus,
    ) =>
        api.patch<WorkActivity>(`/work-activities/${id}/status`, {
            from_status: fromStatus,
            to_status: toStatus,
        }),
}
